import * as React from "react";
import { Sparkles, Warehouse } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Progress,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useChat,
} from "randmarcomps";
import {
  useGetV4ManufacturerByRouteManufacturerIdDocumentsInvoicesQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsPurchaseOrdersQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsReceiptsQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsRequisitionsQuery,
  useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery,
} from "randmar-api-client";
import { FormattedNumber } from "react-intl";
import { Link, useParams } from "react-router-dom";
import VendorValues from "../components/categories/VendorValues";
import InvoicesGrid from "../components/grids/InvoicesGrid";
import PurchaseOrdersGrid from "../components/grids/PurchaseOrdersGrid";
import ReceiptsGrid from "../components/grids/ReceiptsGrid";
import CurrencyUtils from "../components/util/CurrencyUtils";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import FormatDateFromInt, { getCurrentDate } from "../components/util/DateTimeUtil";
import InlineSpinner from "../components/util/InlineSpinner";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import withRoot from "../withRoot";

type OperationsTab = "Overview" | "PurchaseOrders" | "Receipts" | "Invoices";

function buildOperationsAssistantPrompt(options: {
  applicationId: string;
  activeTab: OperationsTab;
  warehousesLoaded: number;
  purchaseOrderLinesLoaded: number;
  receiptLinesLoaded: number;
  invoiceLinesLoaded: number;
  requisitionsLoaded: number;
}) {
  return [
    "You are assisting with the Randmar Manufacturer Portal Operations page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Active tab: ${options.activeTab}`,
    "",
    `Inventory warehouses loaded: ${options.warehousesLoaded}`,
    `Purchase order lines loaded: ${options.purchaseOrderLinesLoaded}`,
    `Receipt lines loaded: ${options.receiptLinesLoaded}`,
    `Invoice lines loaded: ${options.invoiceLinesLoaded}`,
    `Draft requisitions loaded: ${options.requisitionsLoaded}`,
    "",
    "Help me interpret what I'm seeing and recommend next actions.",
  ].join("\n");
}

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const Operations: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { sendMessageWithClear } = useChat();
  const { subSection } = useParams();

  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);

  const inventoryQuery = useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const requisitionsQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsRequisitionsQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const purchaseOrdersQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsPurchaseOrdersQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const receiptsQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsReceiptsQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const invoicesQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsInvoicesQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const inventoryValues = inventoryQuery.data ?? [];
  const requisitions = requisitionsQuery.data ?? [];
  const purchaseOrders = purchaseOrdersQuery.data ?? [];
  const receipts = receiptsQuery.data ?? [];
  const invoices = invoicesQuery.data ?? [];

  const isRefreshing =
    inventoryQuery.isFetching ||
    requisitionsQuery.isFetching ||
    purchaseOrdersQuery.isFetching ||
    receiptsQuery.isFetching ||
    invoicesQuery.isFetching;

  const isLoading =
    inventoryQuery.isLoading ||
    requisitionsQuery.isLoading ||
    purchaseOrdersQuery.isLoading ||
    receiptsQuery.isLoading ||
    invoicesQuery.isLoading;

  const hasLoadedSomeData =
    inventoryQuery.data !== undefined ||
    requisitionsQuery.data !== undefined ||
    purchaseOrdersQuery.data !== undefined ||
    receiptsQuery.data !== undefined ||
    invoicesQuery.data !== undefined;

  React.useEffect(() => {
    if (!isRefreshing && hasLoadedSomeData) {
      setLastRefreshedAt(new Date());
    }
  }, [hasLoadedSomeData, isRefreshing]);

  React.useEffect(() => {
    const previousTitle = document.title;
    if (!previousTitle.toLowerCase().includes("operations")) {
      document.title = `Operations | ${previousTitle}`;
    }

    return () => {
      document.title = previousTitle;
    };
  }, []);

  const handleRefresh = React.useCallback(async () => {
    if (!applicationId) {
      return;
    }

    await Promise.all([
      inventoryQuery.refetch(),
      requisitionsQuery.refetch(),
      purchaseOrdersQuery.refetch(),
      receiptsQuery.refetch(),
      invoicesQuery.refetch(),
    ]);
  }, [applicationId, inventoryQuery, invoicesQuery, purchaseOrdersQuery, receiptsQuery, requisitionsQuery]);

  const currentTab = React.useMemo<OperationsTab>(() => {
    if (subSection === "PurchaseOrders" || subSection === "Receipts" || subSection === "Invoices" || subSection === "Overview") {
      return subSection;
    }
    return "Overview";
  }, [subSection]);

  const todayKey = React.useMemo(() => FormatDateFromInt(getCurrentDate()) || "", []);
  const todayLabel = React.useMemo(() => {
    if (!todayKey) return "today";
    const parsed = new Date(todayKey);
    if (Number.isNaN(parsed.getTime())) return todayKey;
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(parsed);
  }, [todayKey]);

  const totalInventoryValue = React.useMemo(
    () => inventoryValues.reduce((sum, value) => sum + (value.InventoryValue || 0), 0),
    [inventoryValues]
  );
  const warehouseCount = React.useMemo(() => {
    const entries = inventoryValues
      .map((entry) => entry.Location)
      .filter((location): location is string => Boolean(location));
    return new Set(entries).size;
  }, [inventoryValues]);

  const purchaseOrderSummary = React.useMemo(() => {
    const valueByPo = new Map<string, number>();
    for (const line of purchaseOrders) {
      const poNumber = line.PurchaseOrderNumber ?? "";
      if (!poNumber) continue;
      const quantity = line.QuantityOutstanding ?? line.QuantityOrdered ?? 0;
      const unitPrice = line.UnitPrice ?? 0;
      valueByPo.set(poNumber, (valueByPo.get(poNumber) ?? 0) + quantity * unitPrice);
    }

    return {
      count: valueByPo.size,
      value: Array.from(valueByPo.values()).reduce((sum, current) => sum + current, 0),
    };
  }, [purchaseOrders]);

  const todaysReceiptsSummary = React.useMemo(() => {
    const receiptsToday = receipts.filter((receipt) => {
      const normalizedDate =
        typeof receipt.ReceiptDate === "number" ? FormatDateFromInt(receipt.ReceiptDate) : "";
      return !todayKey || normalizedDate === todayKey;
    });

    const receiptNumbers = new Set(
      receiptsToday.map((receipt) => receipt.ReceiptNumber).filter((value): value is string => Boolean(value))
    );

    const totalValue = receiptsToday.reduce(
      (sum, receipt) => sum + (receipt.Quantity ?? 0) * (receipt.UnitPrice ?? 0),
      0
    );

    return { count: receiptNumbers.size, value: totalValue };
  }, [receipts, todayKey]);

  const invoiceBreakdown = React.useMemo(() => {
    const invoiceByNumber = new Map<string, typeof invoices>();
    for (const line of invoices) {
      const invoiceNumber = line.InvoiceNumber ?? "";
      if (!invoiceNumber) continue;
      const existing = invoiceByNumber.get(invoiceNumber);
      if (!existing) {
        invoiceByNumber.set(invoiceNumber, [line]);
      } else {
        existing.push(line);
      }
    }

    const representatives = Array.from(invoiceByNumber.values()).map((lines) =>
      lines.reduce((best, current) => {
        const bestScore = Math.abs(best.Total ?? best.SubTotal ?? 0);
        const currentScore = Math.abs(current.Total ?? current.SubTotal ?? 0);
        return currentScore > bestScore ? current : best;
      }, lines[0])
    );

    const unpaid = representatives.filter((invoice) => invoice.Payed === false);
    const isCredit = (invoice: (typeof representatives)[number]) => {
      const lineType = (invoice.LineType ?? "").toLowerCase();
      if (lineType.includes("credit")) return true;
      return (invoice.Total ?? invoice.SubTotal ?? 0) < 0;
    };

    const invoicesDue = unpaid.filter((invoice) => !isCredit(invoice));
    const creditsOpen = unpaid.filter((invoice) => isCredit(invoice));
    const sumTotals = (list: typeof unpaid) =>
      list.reduce((sum, invoice) => sum + (invoice.AmountDue ?? invoice.SubTotal ?? 0), 0);

    return {
      invoices: { count: invoicesDue.length, value: sumTotals(invoicesDue) },
      credits: { count: creditsOpen.length, value: sumTotals(creditsOpen) },
    };
  }, [invoices]);

  const assistantPrompt = React.useMemo(
    () =>
      buildOperationsAssistantPrompt({
        applicationId,
        activeTab: currentTab,
        warehousesLoaded: warehouseCount,
        purchaseOrderLinesLoaded: purchaseOrders.length,
        receiptLinesLoaded: receipts.length,
        invoiceLinesLoaded: invoices.length,
        requisitionsLoaded: requisitions.length,
      }),
    [applicationId, currentTab, invoices.length, purchaseOrders.length, receipts.length, requisitions.length, warehouseCount]
  );

  const hasErrors =
    inventoryQuery.isError ||
    requisitionsQuery.isError ||
    purchaseOrdersQuery.isError ||
    receiptsQuery.isError ||
    invoicesQuery.isError;

  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";

  const disableSummaryLinks = isLoading || isRefreshing;

  return (
    <div className={pageContainerClassName} aria-busy={isRefreshing}>
      <header className={surfaceClassName} role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                <Warehouse className="h-4 w-4" aria-hidden="true" />
                Manufacturer Portal
              </span>
              {isRefreshing && (
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <InlineSpinner size="sm" decorative />
                  Syncing
                </span>
              )}
            </div>
            <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">Operations</h1>
            <p className="max-w-2xl text-[15px] text-slate-600">
              Monitor inventory and operational documents across Randmar warehouses.
            </p>
            {isRefreshing && <Progress className="mt-3 h-1" aria-label="Refreshing operations data" />}
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={isRefreshing}
              onRefresh={handleRefresh}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => sendMessageWithClear(assistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-4" aria-label="Operations metrics">
          <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Total inventory value
            </p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">
              {disableSummaryLinks ? (
                <Skeleton aria-hidden className="h-8 w-36 rounded-lg" />
              ) : (
                <FormattedNumber value={totalInventoryValue} {...CurrencyUtils.format} />
              )}
            </p>
            <p className="mt-1 text-sm text-slate-600">Across {warehouseCount} warehouses</p>
          </div>

          <Link
            to={Path.purchaseOrders(applicationId)}
            className={cx(
              "rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:bg-gray-50/40",
              disableSummaryLinks && "pointer-events-none opacity-60"
            )}
            aria-disabled={disableSummaryLinks}
            tabIndex={disableSummaryLinks ? -1 : undefined}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">On purchase orders</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">
              {disableSummaryLinks ? (
                <Skeleton aria-hidden className="h-8 w-32 rounded-lg" />
              ) : (
                <FormattedNumber value={purchaseOrderSummary.value} {...CurrencyUtils.format} />
              )}
            </p>
            <p className="mt-1 text-sm text-slate-600">{purchaseOrderSummary.count} open POs</p>
          </Link>

          <Link
            to={Path.receipts(applicationId)}
            className={cx(
              "rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:bg-gray-50/40",
              disableSummaryLinks && "pointer-events-none opacity-60"
            )}
            aria-disabled={disableSummaryLinks}
            tabIndex={disableSummaryLinks ? -1 : undefined}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Received {todayLabel}
            </p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">
              {disableSummaryLinks ? (
                <Skeleton aria-hidden className="h-8 w-28 rounded-lg" />
              ) : (
                <FormattedNumber value={todaysReceiptsSummary.value} {...CurrencyUtils.format} />
              )}
            </p>
            <p className="mt-1 text-sm text-slate-600">{todaysReceiptsSummary.count} receipts logged</p>
          </Link>

          <Link
            to={Path.invoices(applicationId)}
            className={cx(
              "rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:bg-gray-50/40",
              disableSummaryLinks && "pointer-events-none opacity-60"
            )}
            aria-disabled={disableSummaryLinks}
            tabIndex={disableSummaryLinks ? -1 : undefined}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invoices &amp; credits</p>
            <div className="mt-2 space-y-2">
              <div>
                <p className="text-lg font-semibold text-slate-900">
                  {disableSummaryLinks ? (
                    <Skeleton aria-hidden className="h-6 w-24 rounded-lg" />
                  ) : (
                    <FormattedNumber value={invoiceBreakdown.invoices.value} {...CurrencyUtils.format} />
                  )}
                </p>
                <p className="text-sm text-slate-600">{invoiceBreakdown.invoices.count} unpaid invoices</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-900">
                  {disableSummaryLinks ? (
                    <Skeleton aria-hidden className="h-6 w-24 rounded-lg" />
                  ) : (
                    <FormattedNumber value={invoiceBreakdown.credits.value} {...CurrencyUtils.format} />
                  )}
                </p>
                <p className="text-sm text-slate-600">{invoiceBreakdown.credits.count} credits open</p>
              </div>
            </div>
          </Link>
        </div>

        <Alert variant="info" className="mt-6 rounded-2xl border border-gray-100">
          <AlertTitle>What are Operations?</AlertTitle>
          <AlertDescription>
            Inventory value, inbound purchase orders, receipts, and invoice balances — all in one workspace.
          </AlertDescription>
        </Alert>

        {hasErrors && (
          <Alert variant="destructive" className="mt-4 rounded-2xl border border-red-100 bg-white">
            <AlertTitle>Couldn’t load operations data</AlertTitle>
            <AlertDescription>Try refreshing. If the issue persists, contact support.</AlertDescription>
          </Alert>
        )}
      </header>

      <section className={surfaceClassName} aria-label="Operations details">
        <Tabs value={currentTab} className="w-full">
          <TabsList className="flex h-auto w-full flex-wrap items-center justify-start gap-2 rounded-none border-b border-gray-100 bg-transparent p-0">
            <TabsTrigger
              value="Overview"
              className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-none hover:text-slate-900 data-[state=active]:border-b-[var(--ops-accent)] data-[state=active]:text-slate-900"
              asChild
            >
              <Link to={Path.operationsSubSection(applicationId, "Overview")}>Overview</Link>
            </TabsTrigger>
            <TabsTrigger
              value="PurchaseOrders"
              className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-none hover:text-slate-900 data-[state=active]:border-b-[var(--ops-accent)] data-[state=active]:text-slate-900"
              asChild
            >
              <Link to={Path.operationsSubSection(applicationId, "PurchaseOrders")}>Purchase Orders</Link>
            </TabsTrigger>
            <TabsTrigger
              value="Receipts"
              className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-none hover:text-slate-900 data-[state=active]:border-b-[var(--ops-accent)] data-[state=active]:text-slate-900"
              asChild
            >
              <Link to={Path.operationsSubSection(applicationId, "Receipts")}>Receipts</Link>
            </TabsTrigger>
            <TabsTrigger
              value="Invoices"
              className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-none hover:text-slate-900 data-[state=active]:border-b-[var(--ops-accent)] data-[state=active]:text-slate-900"
              asChild
            >
              <Link to={Path.operationsSubSection(applicationId, "Invoices")}>Invoices</Link>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="Overview" className="mt-6 space-y-6">
            <VendorValues inventoryValues={inventoryValues} requisitions={requisitions} />
          </TabsContent>

          <TabsContent value="PurchaseOrders" className="mt-6 space-y-6">
            <PurchaseOrdersGrid purchaseOrders={purchaseOrders} />
          </TabsContent>

          <TabsContent value="Receipts" className="mt-6 space-y-6">
            <ReceiptsGrid receipts={receipts} />
          </TabsContent>

          <TabsContent value="Invoices" className="mt-6 space-y-6">
            <InvoicesGrid invoices={invoices} />
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
};

export default withRoot(Operations);
